import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../components/Icon';
import { Notice } from '../components/Notice';
import type { MovieFrame } from '../domain/model';
import { useObjectUrl } from '../hooks/useObjectUrl';
import type { Stroke } from '../services/magicEraser';
import { eraseFromPhoto, MagicEraserError } from '../services/magicEraser';

interface EraseScreenProps {
  frame: MovieFrame;
  onKeep: (edited: Blob) => void;
  onRestoreOriginal: () => void;
  onClose: () => void;
}

const BRUSH_SIZE = 0.045;

type Phase = { name: 'draw' } | { name: 'working' } | { name: 'result'; edited: Blob } | { name: 'error'; message: string };

/** Full-screen scribble editor: kids paint over the thing to remove, then the eraser fairy does the rest. */
export function EraseScreen({ frame, onKeep, onRestoreOriginal, onClose }: EraseScreenProps) {
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [phase, setPhase] = useState<Phase>({ name: 'draw' });
  const [showOriginal, setShowOriginal] = useState(false);
  const photoUrl = useObjectUrl(frame.blob);
  const editedUrl = useObjectUrl(phase.name === 'result' ? phase.edited : undefined);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = 'rgba(114, 87, 217, 0.65)';
    context.fillStyle = 'rgba(114, 87, 217, 0.65)';
    for (const stroke of strokes) {
      context.lineWidth = stroke.size * canvas.width;
      const first = stroke.points[0];
      if (!first) continue;
      if (stroke.points.length === 1) {
        context.beginPath();
        context.arc(first.x * canvas.width, first.y * canvas.height, context.lineWidth / 2, 0, Math.PI * 2);
        context.fill();
        continue;
      }
      context.beginPath();
      context.moveTo(first.x * canvas.width, first.y * canvas.height);
      for (const point of stroke.points.slice(1)) context.lineTo(point.x * canvas.width, point.y * canvas.height);
      context.stroke();
    }
  }, [strokes]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = frame.width;
    canvas.height = frame.height;
    redraw();
  }, [frame, redraw]);

  useEffect(() => { redraw(); }, [redraw]);

  function pointFromEvent(event: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
    };
  }

  function startStroke(event: React.PointerEvent<HTMLCanvasElement>) {
    if (phase.name !== 'draw') return;
    drawing.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    setStrokes((prev) => [...prev, { points: [pointFromEvent(event)], size: BRUSH_SIZE }]);
  }

  function moveStroke(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const point = pointFromEvent(event);
    setStrokes((prev) => {
      const last = prev[prev.length - 1];
      if (!last) return prev;
      return [...prev.slice(0, -1), { ...last, points: [...last.points, point] }];
    });
  }

  function endStroke() { drawing.current = false; }

  async function makeItDisappear() {
    if (!strokes.length) return;
    setPhase({ name: 'working' });
    try {
      const edited = await eraseFromPhoto(frame.blob, strokes);
      setPhase({ name: 'result', edited });
      setShowOriginal(false);
    } catch (error) {
      setPhase({ name: 'error', message: error instanceof MagicEraserError ? error.message : 'The eraser could not fix this picture. Try a different picture!' });
    }
  }

  return (
    <div className="dialog-shade erase-shade" role="presentation">
      <div className="dialog erase-dialog" role="dialog" aria-modal="true" aria-labelledby="erase-title">
        <h2 id="erase-title">✨ Magic Eraser</h2>
        <p className="erase-badge"><span aria-hidden="true">🌐</span> This helper uses the internet.</p>

        {phase.name === 'draw' && (
          <>
            <p>Scribble on the thing you want to disappear — like a helping hand!</p>
            <div className="erase-stage">
              {photoUrl && <img src={photoUrl} alt="Your picture" />}
              <canvas
                ref={canvasRef}
                className="erase-canvas"
                aria-label="Scribble area"
                onPointerDown={startStroke}
                onPointerMove={moveStroke}
                onPointerUp={endStroke}
                onPointerCancel={endStroke}
              />
            </div>
            <div className="erase-tools">
              <button className="button secondary" type="button" disabled={!strokes.length} onClick={() => setStrokes((prev) => prev.slice(0, -1))}><Icon name="undo" /> Undo</button>
              <button className="button secondary" type="button" disabled={!strokes.length} onClick={() => setStrokes([])}>Clear</button>
            </div>
            <div className="dialog-actions stacked">
              <button className="button primary big-button" type="button" disabled={!strokes.length} onClick={() => { void makeItDisappear(); }}><Icon name="sparkles" /> Make it disappear!</button>
              {frame.originalBlob && <button className="button secondary" type="button" onClick={onRestoreOriginal}>Bring back the real picture</button>}
              <button className="button quiet" type="button" onClick={onClose}>Never mind</button>
            </div>
          </>
        )}

        {phase.name === 'working' && (
          <div className="erase-working" role="status" aria-live="polite">
            <div className="spinner" />
            <p>The eraser fairy is working her magic…</p>
          </div>
        )}

        {phase.name === 'result' && (
          <>
            <p>Ta-da! Tap the picture to compare before and after.</p>
            <button className="erase-stage erase-compare" type="button" onClick={() => setShowOriginal((v) => !v)} aria-pressed={showOriginal} aria-label={showOriginal ? 'Showing the old picture. Tap to see the new one.' : 'Showing the new picture. Tap to see the old one.'}>
              {(showOriginal ? photoUrl : editedUrl) && <img src={showOriginal ? photoUrl ?? '' : editedUrl ?? ''} alt="" />}
              <span className="compare-tag">{showOriginal ? 'Before' : 'After'}</span>
            </button>
            <div className="dialog-actions stacked">
              <button className="button primary big-button" type="button" onClick={() => onKeep(phase.edited)}><Icon name="sparkles" /> Keep it!</button>
              <button className="button secondary" type="button" onClick={() => { setPhase({ name: 'draw' }); setStrokes([]); }}>Try again</button>
              <button className="button quiet" type="button" onClick={onClose}>Never mind</button>
            </div>
          </>
        )}

        {phase.name === 'error' && (
          <>
            <Notice tone="error" role="alert">{phase.message}</Notice>
            <div className="dialog-actions stacked">
              <button className="button primary" type="button" onClick={() => setPhase({ name: 'draw' })}>Try again</button>
              <button className="button quiet" type="button" onClick={onClose}>Never mind</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
